// v1
// Path: frontend/src/components/SyncHub/syncApiService.js
// Inputs: auth token
// Outputs: API call functions for all sync endpoints
// Summary: Centralized API service eliminating duplicated fetch patterns.

const BASE = '/api/sync';

async function apiFetch(path, token, options = {}) {
    const res = await fetch(`${BASE}${path}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...(options.headers || {})
        }
    });
    let payload;
    try {
        payload = await res.json();
    } catch (e) {
        if (!res.ok) throw new Error(`API error ${res.status}: ${res.statusText}`);
        return null;
    }

    if (!res.ok || payload.success === false) {
        throw new Error(payload.error || `API error ${res.status}`);
    }

    // Transparently unwrap { success: true, data: ... }
    if (payload && typeof payload === 'object' && payload.success === true && 'data' in payload) {
        return payload.data;
    }

    return payload;
}

export const syncApi = {
    // Accounts
    getAccounts: (token) => apiFetch('/accounts', token),
    createAccount: (token, data) => apiFetch('/accounts', token, { method: 'POST', body: JSON.stringify(data) }),
    deleteAccount: (token, id) => apiFetch(`/accounts/${id}`, token, { method: 'DELETE' }),
    getAuthUrl: (token, id) => apiFetch(`/auth-url/${id}`, token),
    testConnection: (token, id) => apiFetch(`/test-connection/${id}`, token, { method: 'POST' }),
    getEntities: (token, accountId) => apiFetch(`/accounts/${accountId}/entities`, token),
    getCustomFields: (token, accountId, entityType) => apiFetch(`/accounts/${accountId}/custom-fields?entityType=${entityType}`, token),

    // Configs / Pipelines
    getConfigs: (token) => apiFetch('/configs', token),
    saveConfig: (token, data) => apiFetch('/configs', token, { method: 'POST', body: JSON.stringify(data) }),
    deleteConfig: (token, id) => apiFetch(`/configs/${id}`, token, { method: 'DELETE' }),
    saveFieldMappings: (token, data) => apiFetch('/configs/field-mappings', token, { method: 'POST', body: JSON.stringify(data) }),
    toggleActive: (token, data) => apiFetch('/configs/toggle-active', token, { method: 'POST', body: JSON.stringify(data) }),

    // Sync Execution
    previewSync: (token, data) => apiFetch('/sync/preview', token, { method: 'POST', body: JSON.stringify(data) }),
    executeSync: (token, data) => apiFetch('/sync/execute', token, { method: 'POST', body: JSON.stringify(data) }),
    getLogs: (token) => apiFetch('/sync/logs', token),

    // Previews
    pbsPreview: (token, params) => apiFetch(`/pbs-preview?${new URLSearchParams(params)}`, token),
    qbPreview: (token, params) => apiFetch(`/qb-preview?${new URLSearchParams(params)}`, token),

    // Connection Groups
    getConnectionGroups: (token) => apiFetch('/connection-groups', token),
    createConnectionGroup: (token, data) => apiFetch('/connection-groups', token, { method: 'POST', body: JSON.stringify(data) }),
    updateConnectionGroup: (token, id, data) => apiFetch(`/connection-groups/${id}`, token, { method: 'PUT', body: JSON.stringify(data) }),
    deleteConnectionGroup: (token, id) => apiFetch(`/connection-groups/${id}`, token, { method: 'DELETE' }),
    applyStandardMappings: (token, data) => apiFetch('/connection-group/apply-standard-mappings', token, { method: 'POST', body: JSON.stringify(data) }),

    // Standard Mappings
    getStandardMappings: (token, params) => apiFetch(`/standard-mappings?${new URLSearchParams(params)}`, token),
    saveStandardMapping: (token, data) => apiFetch('/standard-mappings', token, { method: 'POST', body: JSON.stringify(data) }),
    deleteStandardMapping: (token, id) => apiFetch(`/standard-mappings/${id}`, token, { method: 'DELETE' }),
    createStandardSet: (token, data) => apiFetch('/standard-sets', token, { method: 'POST', body: JSON.stringify(data) }),
    renameStandardSet: (token, name, data) => apiFetch(`/standard-sets/${encodeURIComponent(name)}`, token, { method: 'PUT', body: JSON.stringify(data) }),
    setDefaultSet: (token, data) => apiFetch('/standard-sets/set-default', token, { method: 'POST', body: JSON.stringify(data) }),
    deleteStandardSet: (token, name) => apiFetch(`/standard-sets/${encodeURIComponent(name)}`, token, { method: 'DELETE' }),

    // Infrastructure
    getDatabaseProfiles: (token) => apiFetch('/database-profiles', token),
    getDatabases: (token, params) => apiFetch(`/databases?${new URLSearchParams(params)}`, token),
    getDatabaseTables: (token, params) => apiFetch(`/database-tables?${new URLSearchParams(params)}`, token),
    getInstanceCredential: (token, params) => apiFetch(`/instance-credential?${new URLSearchParams(params)}`, token),
    saveInstanceCredential: (token, data) => apiFetch('/instance-credential', token, { method: 'POST', body: JSON.stringify(data) }),
    testInstanceConnection: (token, data) => apiFetch('/test-instance-connection', token, { method: 'POST', body: JSON.stringify(data) }),
    getInstanceInterfaces: (token, params) => apiFetch(`/instance-interfaces?${new URLSearchParams(params)}`, token),
    getFieldDefinitions: (token, params) => apiFetch(`/field-definitions?${new URLSearchParams(params)}`, token),

    // Settings
    getEntityDefaults: (token) => apiFetch('/settings/entity-defaults', token),
    saveEntityDefault: (token, data) => apiFetch('/settings/entity-defaults', token, { method: 'POST', body: JSON.stringify(data) }),
    deleteEntityDefault: (token, id) => apiFetch(`/settings/entity-defaults/${id}`, token, { method: 'DELETE' }),

    // Data Dictionary
    getDataDictionaryTables: (token, params) => apiFetch(`/data-dictionary/tables?${new URLSearchParams(params)}`, token),
    getDataDictionaryFields: (token, tableName) => apiFetch(`/data-dictionary/fields/${encodeURIComponent(tableName)}`, token),
    updateDataDictionaryField: (token, fieldId, data) => apiFetch(`/data-dictionary/fields/${fieldId}`, token, { method: 'PUT', body: JSON.stringify(data) }),
};

export default syncApi;
